import PropTypes from 'prop-types';
import { useState, useRef, useMemo, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import './TablaBitacora.scss';
import { Table, SelectTamanoPagina, Input } from 'components';
import { DATE_FORMAT_COLUMN_TABLE } from 'constants/dateFormat';
import { message } from 'antd';
import { downloadExcelBitacoraSync } from 'actions/files.actions';
import { base64toBlob } from 'utils/file';
import { useDebounce } from 'hooks/useDebounce';
import { filterList } from 'utils/filterList';

export const TablaBitacora = ({
  bitacoraData,
  loadingTable,
  onChangePagination,
  totalRegistros,
  paginationCurrent,
  paginationsPageSize,
  filtrosReporte
}) => {
  const filtersRef = useRef({});
  const [filters, setFilters] = useState({});
  const [downloadingExcel, setDownloadingExcel] = useState(true);
  const [messageApi, contextHolder] = message.useMessage();
  // const { pageSearch, pageSizeSearch } = paginationBusqueda;

  const showTotal = (total, range) => {
    return `Mostrando del ${range[0]} al ${range[1]} de ${total} registros`;
  };

  const [pagination, setPagination] = useState({
    position: ['bottomRight'],
    showQuickJumper: true,
    size: 'small',
    current: 1,
    pageSize: 10,
    showTotal,
    total: 0,
    showSizeChanger: false
  });

  const DEBOUNCE_TIME_FILTER = 2000;
  const applyFilter = useDebounce((fields) => setFilters(fields), DEBOUNCE_TIME_FILTER);
  const dispatch = useDispatch();

  const handleChangeFilter = ({ target }) => {
    const { value, name } = target;
    filtersRef.current[name] = value;
    applyFilter({ ...filtersRef.current });
  };

  const filter = (columnName, lblColumn) => (
    <label htmlFor={columnName}>
      {lblColumn}
      <Input
        id={columnName}
        placeholder="Filtrar"
        name={columnName}
        key={columnName}
        onChange={handleChangeFilter}
      />
    </label>
  );

  const filteredList = useMemo(() => {
    if (bitacoraData !== null && bitacoraData !== undefined) {
      const { origen, nombreUsuario, folioMonitoreo, tipoMonitoreo } = bitacoraData;
      const filteredData = bitacoraData.filter((row) => {
        if (origen && row?.origen !== origen) return false;
        if (tipoMonitoreo && row?.idTipoMonitoreo !== tipoMonitoreo) return false;
        if (folioMonitoreo !== undefined && folioMonitoreo !== row?.folioMonitoreo) return false;
        if (nombreUsuario !== undefined && nombreUsuario !== row?.nombreUsuario) return false;
        setDownloadingExcel(false);
        return true;
      });
      return filteredData;
    }
    setDownloadingExcel(true);
    return null;
  }, [bitacoraData]);

  const dataList = useMemo(() => {
    return filterList(filteredList, filters);
  }, [filteredList, filters]);

  const columns = [
    {
      title: 'ID de la bitácora',
      dataIndex: 'idBitacora',
      width: '8%',
      key: 'idBitacora'
    },
    {
      title: filter('origen', 'Origen'),
      dataIndex: 'origen',
      width: '7%',
      key: 'origen'
    },
    {
      title: 'Tipo de movimiento',
      dataIndex: 'movimiento',
      width: '8%',
      key: 'movimiento'
    },
    {
      title: filter('nombre_usuario', 'Nombre del usuario'),
      dataIndex: 'nombre_usuario',
      width: '10%',
      key: 'nombre_usuario'
    },
    {
      title: 'Rol',
      dataIndex: 'rolUsuario',
      width: '10%',
      key: 'rolUsuario'
    },
    {
      title: filter('folio', 'Folio de monitoreo'),
      dataIndex: 'folio',
      width: '10%',
      key: 'folio'
    },
    {
      title: filter('tipoMonitoreo', 'Tipo de monitoreo'),
      dataIndex: 'tipoMonitoreo',
      width: '10%',
      key: 'tipoMonitoreo'
    },
    {
      title: 'Fecha de sincronización',
      dataIndex: 'fechaHoraFormat',
      render: (d) => d.format(DATE_FORMAT_COLUMN_TABLE),
      width: '10%'
    },
    {
      title: 'Proceso Electoral',
      dataIndex: 'procesoElectoral',
      width: '15%',
      key: 'procesoElectoral'
    },
    {
      title: 'Proceso',
      dataIndex: 'procesoEspecifico',
      width: '15%',
      key: 'procesoEspecifico'
    }
  ];

  const handleChangePageSize = (pageSize) => {
    setPagination((prevPagination) => ({ ...prevPagination, pageSize }));
    onChangePagination(pagination.current, pageSize);
  };

  const handleDownloadExcel = async () => {
    setDownloadingExcel(true);
    const { success, response } = await dispatch(
      downloadExcelBitacoraSync({
        idProcesoElectoral: filtrosReporte.idProcesoElectoral,
        idProcesoEspecifico: filtrosReporte.idProcesoEspecifico,
        idRol: filtrosReporte.idRol,
        fecha_inicio: filtrosReporte.fecha_inicio,
        fecha_fin: filtrosReporte.fecha_fin
      })
    );
    if (response && success) {
      if (response.data.code === 200 && response.data.excel64) {
        const url = window.URL.createObjectURL(
          base64toBlob(response.data.excel64.base64, response.data.excel64.tipo)
        );
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', response.data.excel64.nombre);
        link.click();
      }
      console.log(messageApi);
    }
    setDownloadingExcel(false);
  };

  useEffect(() => {
    setPagination((prev) => ({
      ...prev,
      total: totalRegistros
    }));
  }, [totalRegistros]);

  return (
    <div className="tabla-bitacora">
      <div className="table-bit-header">
        <div>
          <SelectTamanoPagina
            value={pagination.pageSize}
            onChange={handleChangePageSize}
            disableBottomExcel={downloadingExcel}
            onClick={handleDownloadExcel}
          />
        </div>
      </div>
      {contextHolder}
      <Table
        bordered={false}
        scroll={{ x: 2000 }}
        columns={columns}
        dataSource={dataList || bitacoraData}
        loading={loadingTable}
        pagination={{
          ...pagination,
          onChange: (page, pageSize) => {
            paginationCurrent(page);
            paginationsPageSize(pageSize);
            setPagination((prev) => ({
              ...prev,
              current: page,
              pageSize: pageSize || prev.pageSize
            }));
          }
        }}
      />
    </div>
  );
};

TablaBitacora.propTypes = {
  bitacoraData: PropTypes.arrayOf(
    PropTypes.shape({
      idBitacora: PropTypes.number.isRequired,
      folio: PropTypes.string.isRequired,
      tipoMonitoreo: PropTypes.string.isRequired,
      procesoElectoral: PropTypes.string.isRequired,
      procesoEspecifico: PropTypes.string.isRequired,
      rolUsuario: PropTypes.string.isRequired,
      nombre_usuario: PropTypes.string.isRequired,
      fecha_hora: PropTypes.string.isRequired
    })
  ).isRequired,
  loadingTable: PropTypes.bool.isRequired,
  onChangePagination: PropTypes.func.isRequired,
  totalRegistros: PropTypes.number.isRequired,
  paginationCurrent: PropTypes.func.isRequired,
  paginationsPageSize: PropTypes.func.isRequired,
  filtrosReporte: PropTypes.arrayOf(
    PropTypes.shape({
      idProcesoElectoral: PropTypes.number.isRequired,
      idProcesoEspecifico: PropTypes.number.isRequired,
      idRol: PropTypes.number.isRequired,
      fecha_inicio: PropTypes.string.isRequired,
      fecha_fin: PropTypes.string.isRequired,
      pagina: PropTypes.number.isRequired,
      cantidad: PropTypes.number.isRequired
    })
  ).isRequired
};
